"use client";

/**
 * Global unsaved-changes navigation guard: one AlertDialog + `beforeunload`, shared by
 * `useForm` (close/cancel), `useSessionRouter`, `GuardedLink`, `EntityLink`, etc.
 *
 * @see docs/reference/08-client-architecture/05-unsaved-changes-navigation.md
 */

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslations } from "next-intl";

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/overlay/alert-dialog";
import { Button, buttonVariants } from "@/components/primitives/button";
import { cn } from "@/lib/utils";

// MARK: Types
/**
 * Context passed to `useUnsavedChangesNavigation` / `useUnsavedChangesNavigationOptional`.
 * Side effects: `tryRunNavigation` may open a modal; `confirm_discard` runs the pending
 * navigation and closes the dialog; `registerGuard` is called from `useForm` only.
 */
export type UnsavedChangesNavigationContextValue = {
    /** Runs `action` immediately, or opens the discard dialog and runs it after confirm. */
    tryRunNavigation: (action: () => void) => void;
    /**
     * Registers the active form's blocking predicate (null = no unsaved guard).
     * Last registration wins; forms must unregister on unmount.
     */
    registerGuard: (is_blocking: (() => boolean) | null) => void;
    /**
     * Registers submit-for-exit (`useForm`): when the user chooses “Save and quit”, the handler runs;
     * on `true`, pending navigation runs like after “Discard”.
     */
    registerSaveHandler: (save: (() => Promise<boolean>) | null) => void;
    dialog_open: boolean;
    save_exit_available: boolean;
    /** True while “Save and quit” is awaiting the registered handler (disables dialog actions). */
    save_exit_busy: boolean;
    confirm_discard: () => void;
    /** Awaits registered save handler; on success runs the pending navigation. */
    confirm_save_and_exit: () => Promise<void>;
    dismiss_dialog: () => void;
    /**
     * The next `steps` `tryRunNavigation` calls run without opening the dialog (guard not consulted).
     * Used after successful submit when `requestSuccess` runs `detailsPanelActions.open` → nested
     * `session_router.replace` while RHF can still report dirty for a moment.
     */
    suppress_next_navigation_guard_steps: (steps: number) => void;
};

const UnsavedChangesNavigationContext =
    createContext<UnsavedChangesNavigationContextValue | null>(null);

// MARK: Provider
/**
 * Mount once in `[locale]/layout.tsx` (inside `NextIntlClientProvider` so dialog strings resolve).
 *
 * @param props.children - App shell; dialog and `beforeunload` apply to all locale routes.
 */
export function UnsavedChangesNavigationProvider(props: { children: React.ReactNode }) {
    // MARK: Refs — guard predicate from useForm; pending navigation after blocked click
    const guard_ref = useRef<(() => boolean) | null>(null);
    const pending_action_ref = useRef<(() => void) | null>(null);
    /**
     * After "Discard", the pending callback often calls `useSessionRouter` → `tryRunNavigation`
     * again while RHF is still dirty (user has not saved). Without this flag, the second call
     * would re-open the dialog instead of navigating. We set true in `confirm_discard` before
     * `fn()`; the next `tryRunNavigation` consumes it and runs the action without re-checking.
     */
    const skip_next_guard_ref = useRef(false);
    /** Remaining `tryRunNavigation` calls that skip the guard (see `suppress_next_navigation_guard_steps`). */
    const suppress_guard_steps_remaining_ref = useRef(0);
    const save_handler_ref = useRef<(() => Promise<boolean>) | null>(null);
    const save_exit_busy_ref = useRef(false);
    const [dialog_open, set_dialog_open] = useState(false);
    const [save_exit_available, set_save_exit_available] = useState(false);
    const [save_exit_busy, set_save_exit_busy] = useState(false);

    // MARK: registerGuard
    const registerGuard = useCallback((is_blocking: (() => boolean) | null) => {
        guard_ref.current = is_blocking;
    }, []);

    // MARK: registerSaveHandler
    const registerSaveHandler = useCallback((save: (() => Promise<boolean>) | null) => {
        save_handler_ref.current = save;
        set_save_exit_available(!!save);
    }, []);

    // MARK: suppress_next_navigation_guard_steps
    const suppress_next_navigation_guard_steps = useCallback((steps: number) => {
        suppress_guard_steps_remaining_ref.current = Math.max(0, Math.floor(steps));
    }, []);

    // MARK: confirm_discard
    /**
     * Clears pending ref, closes dialog, runs the stashed navigation (e.g. `router.replace`).
     * `finally` clears `skip_next_guard_ref` if `fn()` used raw `router.push` (no `tryRunNavigation`).
     */
    const confirm_discard = useCallback(() => {
        const fn = pending_action_ref.current;
        pending_action_ref.current = null;
        set_dialog_open(false);
        if (!fn) {
            return;
        }
        skip_next_guard_ref.current = true;
        try {
            fn();
        } finally {
            if (skip_next_guard_ref.current) {
                skip_next_guard_ref.current = false;
            }
        }
    }, []);

    // MARK: dismiss_dialog
    /** User chose "Stay" or closed overlay — cancel pending navigation only. */
    const dismiss_dialog = useCallback(() => {
        if (save_exit_busy_ref.current) {
            return;
        }
        pending_action_ref.current = null;
        set_dialog_open(false);
    }, []);

    // MARK: confirm_save_and_exit
    const confirm_save_and_exit = useCallback(async () => {
        const save = save_handler_ref.current;
        if (!save || save_exit_busy_ref.current) {
            return;
        }
        save_exit_busy_ref.current = true;
        set_save_exit_busy(true);
        try {
            const ok = await save();
            if (ok) {
                confirm_discard();
            }
        } finally {
            save_exit_busy_ref.current = false;
            set_save_exit_busy(false);
        }
    }, [confirm_discard]);

    // MARK: tryRunNavigation
    const tryRunNavigation = useCallback((action: () => void) => {
        if (skip_next_guard_ref.current) {
            skip_next_guard_ref.current = false;
            action();
            return;
        }
        if (suppress_guard_steps_remaining_ref.current > 0) {
            suppress_guard_steps_remaining_ref.current -= 1;
            action();
            return;
        }
        const should_block = guard_ref.current?.() === true;
        if (!should_block) {
            action();
            return;
        }
        pending_action_ref.current = action;
        set_dialog_open(true);
    }, []);

    const value = useMemo<UnsavedChangesNavigationContextValue>(
        () => ({
            tryRunNavigation: tryRunNavigation,
            registerGuard,
            registerSaveHandler,
            dialog_open,
            save_exit_available,
            save_exit_busy,
            confirm_discard,
            confirm_save_and_exit,
            dismiss_dialog,
            suppress_next_navigation_guard_steps,
        }),
        [
            tryRunNavigation,
            registerGuard,
            registerSaveHandler,
            dialog_open,
            save_exit_available,
            save_exit_busy,
            confirm_discard,
            confirm_save_and_exit,
            dismiss_dialog,
            suppress_next_navigation_guard_steps,
        ],
    );

    // MARK: beforeunload — tab close / refresh (browser controls copy)
    useEffect(() => {
        const on_before_unload = (e: BeforeUnloadEvent) => {
            if (guard_ref.current?.()) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", on_before_unload);
        return () => window.removeEventListener("beforeunload", on_before_unload);
    }, []);

    return (
        <UnsavedChangesNavigationContext.Provider value={value}>
            {props.children}
            <UnsavedChangesNavigationDialog />
        </UnsavedChangesNavigationContext.Provider>
    );
}

// MARK: Dialog UI
/**
 * Alert dialog bound to `dialog_open`; discard uses `AlertDialogAction` without `preventDefault`
 * because Radix composes our `onClick` before `onOpenChange(false)` — if `defaultPrevented`,
 * the dialog does not close (see `composeEventHandlers` in `@radix-ui/primitive`).
 */
function UnsavedChangesNavigationDialog() {
    const ctx = useContext(UnsavedChangesNavigationContext);
    const t = useTranslations("ui.unsaved_form_exit");
    if (!ctx) {
        return null;
    }

    return (
        <AlertDialog open={ctx.dialog_open} onOpenChange={(open) => !open && ctx.dismiss_dialog()}>
            <AlertDialogContent
                className={cn("w-full max-w-2xl sm:max-w-2xl")}
            >
                <AlertDialogHeader>
                    <AlertDialogTitle>{t("title")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("description")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <AlertDialogCancel
                        type="button"
                        disabled={ctx.save_exit_busy}
                        onClick={ctx.dismiss_dialog}
                    >
                        {t("stay")}
                    </AlertDialogCancel>
                    {ctx.save_exit_available ? (
                        <Button
                            type="button"
                            variant="default"
                            disabled={ctx.save_exit_busy}
                            onClick={() => {
                                void ctx.confirm_save_and_exit();
                            }}
                        >
                            {ctx.save_exit_busy ? t("saving") : t("save_and_quit")}
                        </Button>
                    ) : null}
                    <AlertDialogAction
                        type="button"
                        disabled={ctx.save_exit_busy}
                        className={cn(buttonVariants({ variant: "destructive" }))}
                        onClick={() => {
                            ctx.confirm_discard();
                        }}
                    >
                        {t("discard")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

// MARK: Hooks
/**
 * @throws If used outside `UnsavedChangesNavigationProvider` (e.g. `useForm` in locale app).
 */
export function useUnsavedChangesNavigation(): UnsavedChangesNavigationContextValue {
    const ctx = useContext(UnsavedChangesNavigationContext);
    if (!ctx) {
        throw new Error("useUnsavedChangesNavigation must be used within UnsavedChangesNavigationProvider");
    }
    return ctx;
}

/**
 * Same shape as `useUnsavedChangesNavigation` but returns null when outside the provider
 * (e.g. Storybook or routes without the locale wrapper). Callers must fall back to unguarded navigation.
 */
export function useUnsavedChangesNavigationOptional(): UnsavedChangesNavigationContextValue | null {
    return useContext(UnsavedChangesNavigationContext);
}
